/**
 * @fileoverview
 * Shared Image Service - Handles image URL fetching across all modules
 * 
 * @version 1.1.0
 * @author KATALYST TEAM
 */

import { Injectable, inject, signal } from '@angular/core';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import { ApiService } from '@core/http';
import { API_ENDPOINTS } from '@core/constants';
import type { TableRow } from '@shared/components/table/table.model';
import {
  DEFAULT_TABLE_IMAGE_UUID_FIELDS,
  resolveTableRowImageUuid,
  resolveTableRowPreviewUrl,
} from '@core/utils/table-image.util';

export class TableRowImageError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TableRowImageError';
  }
}

export interface ImageUrlResponse {
  uuid: string;
  url: string;
  expiresAt: string;
  expiresIn: number;
}

@Injectable({
  providedIn: 'root'
})
export class ImageService {
  private api = inject(ApiService);

  // Popup state signals
  readonly isPopupOpen = signal(false);
  readonly popupImageUrl = signal<string | null>(null);
  readonly popupLoading = signal(false);
  readonly popupTitle = signal('Image Preview');

  /**
   * Get presigned image URL by UUID
   * @param uuid - The image UUID
   * @returns Observable with the presigned URL
   */
  getImageUrl(uuid: string): Observable<ImageUrlResponse> {
    return this.api.get<ImageUrlResponse>(API_ENDPOINTS.AUDITS.FETCH_IMAGE_URL(uuid)).pipe(
      catchError(error => {
        console.error('Failed to fetch image URL:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Open image in a new tab
   * @param uuid - The image UUID
   */
  openImageInNewTab(uuid: string): void {
    this.getImageUrl(uuid).subscribe({
      next: (response) => {
        window.open(response.url, '_blank');
      },
      error: (error) => {
        console.error('Failed to open image:', error);
      }
    });
  }

  /**
   * Open image in popup modal
   * @param uuid - The image UUID
   * @param title - Optional title for the popup
   */
  openImageInPopup(uuid: string, title?: string): void {
    this.popupLoading.set(true);
    this.popupImageUrl.set(null);
    this.popupTitle.set(title || 'Image Preview');
    this.isPopupOpen.set(true);

    this.getImageUrl(uuid).subscribe({
      next: (response) => {
        this.popupImageUrl.set(response.url);
        this.popupLoading.set(false);
      },
      error: (error) => {
        console.error('Failed to load image:', error);
        this.popupLoading.set(false);
        this.closePopup();
      }
    });
  }

  /**
   * Open image in popup with a direct URL (no API call)
   * @param url - The image URL
   * @param title - Optional title for the popup
   */
  openImageUrlInPopup(url: string, title?: string): void {
    this.popupImageUrl.set(url);
    this.popupTitle.set(title || 'Image Preview');
    this.popupLoading.set(false);
    this.isPopupOpen.set(true);
  }

  /**
   * Resolve a display URL for a table row: local preview first, then fetch by UUID.
   */
  viewTableRowImage(
    row: TableRow,
    uuidFields: readonly string[] = DEFAULT_TABLE_IMAGE_UUID_FIELDS,
  ): Observable<string> {
    const previewUrl = resolveTableRowPreviewUrl(row);
    if (previewUrl) {
      return of(previewUrl);
    }

    const uuid = resolveTableRowImageUuid(row, uuidFields);
    if (!uuid) {
      return throwError(() => new TableRowImageError('NO_IMAGE'));
    }

    return this.getImageUrl(uuid).pipe(map((res) => res.url));
  }

  /**
   * Close the image popup
   */
  closePopup(): void {
    this.isPopupOpen.set(false);
    this.popupImageUrl.set(null);
    this.popupLoading.set(false);
  }
}
